import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Save, ArrowLeft, Eye, Code, FileText, Search, Settings, 
  Globe, Share2, CheckCircle, AlertCircle, Info, Type, ToggleLeft
} from 'lucide-react';
import { toast } from 'sonner';
import TipTapEditor from '../../components/cms/TipTapEditor';
import MediaLibrary from '../../components/cms/MediaLibrary';

const API_BASE = window.location.origin || '';

const AdminPageEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [previewHtml, setPreviewHtml] = useState('');
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'source'
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);
  const [imageInsertCallback, setImageInsertCallback] = useState(null);
  const [jsonError, setJsonError] = useState('');
  
  const [formData, setFormData] = useState({
    url_path: '',
    status: 'draft',
    lang: 'en',
    seo_title: '',
    meta_description: '',
    canonical_url: '',
    robots_meta: 'index, follow',
    og_title: '',
    og_description: '',
    og_image: '',
    twitter_title: '',
    twitter_description: '',
    twitter_image: '',
    html_content: '',
    json_ld: '',
    show_in_footer: false,
    footer_group: '',
    footer_label: '',
    footer_order: 99,
    // Blog fields
    page_type: 'page',
    blog_cover_image: '',
    blog_excerpt: '',
    blog_author: 'GoTurkeyTransfer',
    blog_category: '',
    blog_tags: '',
    blog_read_time: '',
    blog_published_date: ''
  });

  const titleLength = (formData.seo_title || '').length;
  const descLength = (formData.meta_description || '').length;
  const titleOptimal = titleLength >= 50 && titleLength <= 60;
  const descOptimal = descLength >= 150 && descLength <= 160;

  useEffect(() => {
    const token = localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    if (!isNew) {
      fetchPage();
    }
  }, [id, isNew, navigate]);

  const fetchPage = async () => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/cms/admin/pages/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.page) {
        const page = data.page;
        setFormData({
          url_path: page.url_path || '',
          status: page.status || 'draft',
          lang: page.lang || 'en',
          seo_title: page.seo_title || '',
          meta_description: page.meta_description || '',
          canonical_url: page.canonical_url || '',
          robots_meta: page.robots_meta || 'index, follow',
          og_title: page.og_title || '',
          og_description: page.og_description || '',
          og_image: page.og_image || '',
          twitter_title: page.twitter_title || '',
          twitter_description: page.twitter_description || '',
          twitter_image: page.twitter_image || '',
          html_content: page.html_content || '',
          json_ld: typeof page.json_ld === 'object' ? JSON.stringify(page.json_ld, null, 2) : (page.json_ld || ''),
          show_in_footer: page.show_in_footer || false,
          footer_group: page.footer_group || '',
          footer_label: page.footer_label || '',
          footer_order: page.footer_order || 99,
          // Blog fields
          page_type: page.page_type || 'page',
          blog_cover_image: page.blog_cover_image || '',
          blog_excerpt: page.blog_excerpt || '',
          blog_author: page.blog_author || 'GoTurkeyTransfer',
          blog_category: page.blog_category || '',
          blog_tags: Array.isArray(page.blog_tags) ? page.blog_tags.join(', ') : (page.blog_tags || ''),
          blog_read_time: page.blog_read_time || '',
          blog_published_date: page.blog_published_date ? page.blog_published_date.split('T')[0] : ''
        });
      }
    } catch (error) {
      toast.error('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'json_ld') {
      validateJsonLd(value);
    }
  }, []);

  const validateJsonLd = (value) => {
    if (!value || !value.trim()) {
      setJsonError('');
      return true;
    }
    try {
      JSON.parse(value);
      setJsonError('');
      return true;
    } catch (e) {
      setJsonError(`Invalid JSON: ${e.message}`);
      return false;
    }
  };

  const handleSave = async () => {
    if (!formData.url_path) {
      toast.error('URL path is required');
      return;
    }

    if (formData.json_ld && !validateJsonLd(formData.json_ld)) {
      toast.error('Please fix JSON-LD errors before saving');
      setActiveTab('schema');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      
      let jsonLd = null;
      if (formData.json_ld) {
        try {
          jsonLd = JSON.parse(formData.json_ld);
        } catch (e) {
          toast.error('Invalid JSON-LD format');
          setSaving(false);
          return;
        }
      }

      const payload = {
        ...formData,
        json_ld: jsonLd,
        footer_order: parseInt(formData.footer_order) || 99,
        // Process blog tags from comma-separated string to array
        blog_tags: formData.blog_tags ? formData.blog_tags.split(',').map(t => t.trim()).filter(t => t) : []
      };

      const url = isNew 
        ? `${API_BASE}/api/cms/admin/pages`
        : `${API_BASE}/api/cms/admin/pages/${id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        toast.success(isNew ? 'Page created' : 'Page saved');
        if (isNew && data.id) {
          navigate(`/admin/pages/edit/${data.id}`);
        }
      } else {
        toast.error(data.detail || 'Failed to save page');
      }
    } catch (error) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updatePreview = useCallback(() => {
    setPreviewHtml(formData.html_content);
  }, [formData.html_content]);

  useEffect(() => {
    if (activeTab === 'preview') {
      updatePreview();
    }
  }, [activeTab, updatePreview]);

  const handleImageInsert = (callback) => {
    setImageInsertCallback(() => callback);
    setMediaLibraryOpen(true);
  };

  const handleMediaSelect = (url, alt) => {
    if (imageInsertCallback) {
      imageInsertCallback(url, alt);
      setImageInsertCallback(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
        <AdminSidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/pages')} data-testid="back-btn" className="text-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <h1 className="text-2xl font-bold text-foreground">
                {isNew ? 'New Page' : 'Edit Page'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="px-3 py-2 bg-secondary border border-border rounded-md text-foreground text-sm"
                data-testid="status-select"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
              <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>

          {/* URL Path */}
          <Card className="bg-secondary border-border mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-foreground whitespace-nowrap">URL Path</label>
                <Input
                  value={formData.url_path}
                  onChange={(e) => handleChange('url_path', e.target.value)}
                  placeholder="/example-page/"
                  className="flex-1 bg-background text-foreground"
                  data-testid="url-path-input"
                />
                {formData.status === 'published' && formData.url_path && (
                  <a 
                    href={formData.url_path.startsWith('/') ? formData.url_path : `/${formData.url_path}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-sm flex items-center gap-1"
                  >
                    <Eye className="h-4 w-4" /> View
                  </a>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-secondary border-border mb-6 flex-wrap">
              <TabsTrigger value="content" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Type className="h-4 w-4" /> Content
              </TabsTrigger>
              <TabsTrigger value="blog" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <FileText className="h-4 w-4" /> Blog
              </TabsTrigger>
              <TabsTrigger value="seo" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Search className="h-4 w-4" /> SEO
              </TabsTrigger>
              <TabsTrigger value="social" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Share2 className="h-4 w-4" /> Social
              </TabsTrigger>
              <TabsTrigger value="footer" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Settings className="h-4 w-4" /> Footer
              </TabsTrigger>
              <TabsTrigger value="schema" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Code className="h-4 w-4" /> Schema
              </TabsTrigger>
              <TabsTrigger value="preview" className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Eye className="h-4 w-4" /> Preview
              </TabsTrigger>
            </TabsList>

            {/* Content Tab - WYSIWYG + Source Toggle */}
            <TabsContent value="content">
              <Card className="bg-secondary border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Type className="h-5 w-5" /> Page Content
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground mr-2">
                        {editorMode === 'visual' ? 'Visual Editor' : 'HTML Source'}
                      </span>
                      <button
                        onClick={() => setEditorMode(editorMode === 'visual' ? 'source' : 'visual')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                          editorMode === 'source' 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                        data-testid="editor-mode-toggle"
                      >
                        <Code className="h-4 w-4" />
                        HTML
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {editorMode === 'visual' ? (
                    <TipTapEditor
                      content={formData.html_content}
                      onChange={(html) => handleChange('html_content', html)}
                      onImageInsert={handleImageInsert}
                    />
                  ) : (
                    <div className="bg-background rounded-md border border-border overflow-hidden">
                      <div className="bg-muted px-4 py-2 border-b border-border flex items-center justify-between">
                        <span className="text-xs text-muted-foreground font-mono">HTML Source Editor</span>
                        <span className="text-xs text-muted-foreground">
                          {(formData.html_content || '').length} characters
                        </span>
                      </div>
                      <textarea
                        value={formData.html_content}
                        onChange={(e) => handleChange('html_content', e.target.value)}
                        className="w-full h-[500px] p-4 bg-background text-foreground font-mono text-sm resize-none focus:outline-none"
                        placeholder="<div class='cms-page'>..."
                        spellCheck={false}
                        data-testid="html-editor"
                      />
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    Script tags, iframes, and event handlers are automatically removed for security.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Blog Tab - For blog posts */}
            <TabsContent value="blog">
              <Card className="bg-secondary border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <FileText className="h-5 w-5" /> Blog Settings
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enable blog mode to display this page in the blog listing at /blog
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Page Type Toggle */}
                  <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
                    <div>
                      <label className="text-sm font-medium text-foreground">Page Type</label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Blog posts appear in /blog with cover images
                      </p>
                    </div>
                    <select
                      value={formData.page_type}
                      onChange={(e) => handleChange('page_type', e.target.value)}
                      className="px-3 py-2 bg-secondary border border-border rounded-md text-foreground text-sm"
                      data-testid="page-type-select"
                    >
                      <option value="page">Regular Page</option>
                      <option value="blog">Blog Post</option>
                    </select>
                  </div>

                  {formData.page_type === 'blog' && (
                    <>
                      {/* Cover Image */}
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">Cover Image URL</label>
                        <div className="flex gap-2">
                          <Input
                            value={formData.blog_cover_image}
                            onChange={(e) => handleChange('blog_cover_image', e.target.value)}
                            placeholder="https://example.com/image.jpg"
                            className="flex-1 bg-background text-foreground border-border"
                            data-testid="cover-image-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleImageInsert((url) => handleChange('blog_cover_image', url))}
                          >
                            Media
                          </Button>
                        </div>
                        {formData.blog_cover_image && (
                          <div className="mt-3 relative rounded-lg overflow-hidden border border-border">
                            <img 
                              src={formData.blog_cover_image} 
                              alt="Cover preview" 
                              className="w-full h-48 object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Recommended size: 1200x630px (16:9 ratio)</p>
                      </div>

                      {/* Excerpt */}
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">
                          Excerpt
                          <span className="text-xs text-muted-foreground ml-2">
                            ({(formData.blog_excerpt || '').length}/200)
                          </span>
                        </label>
                        <textarea
                          value={formData.blog_excerpt}
                          onChange={(e) => handleChange('blog_excerpt', e.target.value)}
                          placeholder="A short summary of this blog post..."
                          maxLength={200}
                          className="w-full h-24 p-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                          data-testid="excerpt-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Short summary shown in blog listing</p>
                      </div>

                      {/* Author & Category Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">Author</label>
                          <Input
                            value={formData.blog_author}
                            onChange={(e) => handleChange('blog_author', e.target.value)}
                            placeholder="GoTurkeyTransfer"
                            className="bg-background text-foreground border-border"
                            data-testid="author-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">Category</label>
                          <Input
                            value={formData.blog_category}
                            onChange={(e) => handleChange('blog_category', e.target.value)}
                            placeholder="Travel Tips"
                            className="bg-background text-foreground border-border"
                            data-testid="category-input"
                          />
                          <p className="text-xs text-muted-foreground mt-1">E.g., Travel Tips, Destinations, Airport Guides</p>
                        </div>
                      </div>

                      {/* Tags & Read Time Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">Tags</label>
                          <Input
                            value={formData.blog_tags}
                            onChange={(e) => handleChange('blog_tags', e.target.value)}
                            placeholder="istanbul, airport, travel"
                            className="bg-background text-foreground border-border"
                            data-testid="tags-input"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Comma-separated tags</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-2">Read Time</label>
                          <Input
                            value={formData.blog_read_time}
                            onChange={(e) => handleChange('blog_read_time', e.target.value)}
                            placeholder="5 min read"
                            className="bg-background text-foreground border-border"
                            data-testid="read-time-input"
                          />
                        </div>
                      </div>

                      {/* Published Date */}
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">Published Date</label>
                        <Input
                          type="date"
                          value={formData.blog_published_date}
                          onChange={(e) => handleChange('blog_published_date', e.target.value)}
                          className="bg-background text-foreground border-border w-64"
                          data-testid="published-date-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Leave empty to use creation date</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* SEO Tab - Fixed Dark Mode Visibility */}
            <TabsContent value="seo">
              <Card className="bg-secondary border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Search className="h-5 w-5" /> SEO Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Meta Title */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-foreground">Meta Title</label>
                      <span className={`text-xs flex items-center gap-1 ${titleOptimal ? 'text-green-500' : titleLength > 60 ? 'text-red-500' : 'text-yellow-500'}`}>
                        {titleOptimal ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                        {titleLength}/60 characters
                      </span>
                    </div>
                    <Input
                      value={formData.seo_title}
                      onChange={(e) => handleChange('seo_title', e.target.value)}
                      placeholder="Page Title | GoTurkeyTransfer"
                      className="bg-background text-foreground placeholder:text-muted-foreground border-border focus:ring-primary"
                      data-testid="seo-title-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Optimal: 50-60 characters</p>
                  </div>

                  {/* Meta Description */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-foreground">Meta Description</label>
                      <span className={`text-xs flex items-center gap-1 ${descOptimal ? 'text-green-500' : descLength > 160 ? 'text-red-500' : 'text-yellow-500'}`}>
                        {descOptimal ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                        {descLength}/160 characters
                      </span>
                    </div>
                    <textarea
                      value={formData.meta_description}
                      onChange={(e) => handleChange('meta_description', e.target.value)}
                      placeholder="A brief description of this page for search engines..."
                      className="w-full h-24 p-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                      data-testid="meta-desc-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Optimal: 150-160 characters</p>
                  </div>

                  {/* Canonical URL */}
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Canonical URL</label>
                    <Input
                      value={formData.canonical_url}
                      onChange={(e) => handleChange('canonical_url', e.target.value)}
                      placeholder="https://goturkeytransfer.com/page-url/"
                      className="bg-background text-foreground placeholder:text-muted-foreground border-border"
                    />
                  </div>

                  {/* Robots Meta */}
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Robots Meta</label>
                    <select
                      value={formData.robots_meta}
                      onChange={(e) => handleChange('robots_meta', e.target.value)}
                      className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                      <option value="index, follow">index, follow (default)</option>
                      <option value="noindex, follow">noindex, follow</option>
                      <option value="index, nofollow">index, nofollow</option>
                      <option value="noindex, nofollow">noindex, nofollow</option>
                    </select>
                  </div>

                  {/* SEO Preview */}
                  <div className="p-4 bg-background rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-3 font-medium">Google Search Preview</p>
                    <div className="space-y-1">
                      <p className="text-[#8ab4f8] text-lg truncate hover:underline cursor-pointer">
                        {formData.seo_title || 'Page Title | GoTurkeyTransfer'}
                      </p>
                      <p className="text-[#bdc1c6] text-sm truncate">
                        goturkeytransfer.com{formData.url_path || '/page-url/'}
                      </p>
                      <p className="text-[#9aa0a6] text-sm line-clamp-2">
                        {formData.meta_description || 'Meta description will appear here...'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Social Tab */}
            <TabsContent value="social">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-secondary border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Globe className="h-5 w-5" /> Open Graph
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">OG Title</label>
                      <Input
                        value={formData.og_title}
                        onChange={(e) => handleChange('og_title', e.target.value)}
                        placeholder={formData.seo_title || 'Page Title'}
                        className="bg-background text-foreground placeholder:text-muted-foreground"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">OG Description</label>
                      <textarea
                        value={formData.og_description}
                        onChange={(e) => handleChange('og_description', e.target.value)}
                        placeholder={formData.meta_description || 'Description...'}
                        className="w-full h-20 p-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground text-sm resize-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">OG Image URL</label>
                      <Input
                        value={formData.og_image}
                        onChange={(e) => handleChange('og_image', e.target.value)}
                        placeholder="https://goturkeytransfer.com/og-image.jpg"
                        className="bg-background text-foreground placeholder:text-muted-foreground"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-secondary border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <Share2 className="h-5 w-5" /> Twitter Card
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">Twitter Title</label>
                      <Input
                        value={formData.twitter_title}
                        onChange={(e) => handleChange('twitter_title', e.target.value)}
                        placeholder={formData.og_title || formData.seo_title || 'Page Title'}
                        className="bg-background text-foreground placeholder:text-muted-foreground"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">Twitter Description</label>
                      <textarea
                        value={formData.twitter_description}
                        onChange={(e) => handleChange('twitter_description', e.target.value)}
                        placeholder={formData.og_description || formData.meta_description || 'Description...'}
                        className="w-full h-20 p-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground text-sm resize-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-2">Twitter Image URL</label>
                      <Input
                        value={formData.twitter_image}
                        onChange={(e) => handleChange('twitter_image', e.target.value)}
                        placeholder={formData.og_image || "https://goturkeytransfer.com/twitter-image.jpg"}
                        className="bg-background text-foreground placeholder:text-muted-foreground"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Footer Tab */}
            <TabsContent value="footer">
              <Card className="bg-secondary border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Settings className="h-5 w-5" /> Footer Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="show_in_footer"
                      checked={formData.show_in_footer}
                      onChange={(e) => handleChange('show_in_footer', e.target.checked)}
                      className="w-4 h-4 rounded border-border accent-primary"
                    />
                    <label htmlFor="show_in_footer" className="text-foreground">
                      Show this page in footer navigation
                    </label>
                  </div>

                  {formData.show_in_footer && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-7">
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">Footer Group</label>
                        <select
                          value={formData.footer_group}
                          onChange={(e) => handleChange('footer_group', e.target.value)}
                          className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                        >
                          <option value="">Select group...</option>
                          <option value="best_airports">Best Airports</option>
                          <option value="services">Services</option>
                          <option value="company">Company</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">Footer Label</label>
                        <Input
                          value={formData.footer_label}
                          onChange={(e) => handleChange('footer_label', e.target.value)}
                          placeholder="Link text in footer"
                          className="bg-background text-foreground"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-foreground block mb-2">Sort Order</label>
                        <Input
                          type="number"
                          value={formData.footer_order}
                          onChange={(e) => handleChange('footer_order', e.target.value)}
                          placeholder="1"
                          className="bg-background text-foreground"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Schema Tab - Separate JSON-LD Editor with Validation */}
            <TabsContent value="schema">
              <Card className="bg-secondary border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-foreground flex items-center gap-2">
                      <FileText className="h-5 w-5" /> JSON-LD Schema
                    </CardTitle>
                    {jsonError ? (
                      <span className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {jsonError}
                      </span>
                    ) : formData.json_ld ? (
                      <span className="text-xs text-green-500 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> Valid JSON
                      </span>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`bg-background rounded-md border overflow-hidden ${jsonError ? 'border-red-500' : 'border-border'}`}>
                    <div className="bg-muted px-4 py-2 border-b border-border flex items-center justify-between">
                      <span className="text-xs text-muted-foreground font-mono">JSON-LD Editor (Structured Data)</span>
                      <button
                        onClick={() => {
                          try {
                            const formatted = JSON.stringify(JSON.parse(formData.json_ld), null, 2);
                            handleChange('json_ld', formatted);
                          } catch (e) {}
                        }}
                        className="text-xs text-primary hover:underline"
                        disabled={jsonError}
                      >
                        Format JSON
                      </button>
                    </div>
                    <textarea
                      value={formData.json_ld}
                      onChange={(e) => handleChange('json_ld', e.target.value)}
                      className="w-full h-[400px] p-4 bg-background text-foreground font-mono text-sm resize-none focus:outline-none"
                      placeholder={`{
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Page Title",
  "description": "Page description",
  "url": "https://goturkeytransfer.com/page-url/"
}`}
                      spellCheck={false}
                      data-testid="json-ld-editor"
                    />
                  </div>
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">
                      <Info className="h-3 w-3 inline mr-1" />
                      JSON-LD schema is rendered separately in the page &lt;head&gt; as a script tag.
                      It should NOT be included in the HTML content above.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Preview Tab */}
            <TabsContent value="preview">
              <Card className="bg-secondary border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Eye className="h-5 w-5" /> Content Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-background rounded-lg p-6 min-h-[400px] prose prose-invert max-w-none">
                    {previewHtml ? (
                      <div 
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                        className="prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary"
                      />
                    ) : (
                      <p className="text-muted-foreground text-center py-12">
                        No content to preview. Add content in the Content tab.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Media Library Modal */}
      <MediaLibrary
        open={mediaLibraryOpen}
        onOpenChange={setMediaLibraryOpen}
        onSelect={handleMediaSelect}
      />
    </div>
  );
};

export default AdminPageEditorPage;
